/**
 * A duration, in the user's language: "20 minutes", "4 hours", "1 hour
 * 5 minutes".
 *
 * Shared, because two unrelated features refuse with a countdown — a Cue
 * cooldown and the login-code send budget — and the alternative was four
 * generic unit strings duplicated across seven locales, which drift.
 *
 * The unit keys still live under `social.cues.*`. That is where they were
 * written and the strings themselves say nothing about Cues, so moving them
 * would churn seven locale files to no effect the user can see. Filing, not
 * coupling.
 *
 * The arithmetic — how a number of seconds becomes one or two parts — is
 * `cue-cooldown-format.ts`, which has no imports and is tested directly. This
 * module only puts language on it.
 */
import i18n from "@/i18n";

import { countdownParts } from "./cue-cooldown-format";

/**
 * Spelled out rather than built from the unit name, so every key here is one
 * the translation types actually check.
 */
const UNIT_KEYS = {
  underMinute: "social.cues.countdownUnderMinute",
  minutes: "social.cues.countdownMinutes",
  hours: "social.cues.countdownHours",
} as const;

/** Localizes the parts `countdownParts` produced. */
export function formatRetryCountdown(seconds: number): string {
  const [major, minor] = countdownParts(seconds).map((part) =>
    i18n.t(UNIT_KEYS[part.unit], { count: part.count }),
  );

  return minor === undefined
    ? major
    : i18n.t("social.cues.countdownPair", { major, minor });
}

